import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, CheckCircle, XCircle, UserCheck, UserX, Phone, MapPin, Building2, Clock, Trash2, Package, Eye, Plus, School, UserCircle, Receipt, X, Edit, Key, Loader2, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { categoryStore, Organization, CourseCategory, Level, InstitutionPrice } from '../../store/categoryStore';
import { usePaperValidation } from '../../hooks/usePaperValidation';
import { formatDateTime } from '../../utils/timeUtils';
import OrderList from '../../components/OrderList';
import PackageGrantModal from '../../components/PackageGrantModal';
import AuthConfirmModal from '../../components/AuthConfirmModal';

const regionData: Record<string, string[]> = {
  '北京': ['北京市'], '上海': ['上海市'], '天津': ['天津市'], '重庆': ['重庆市'],
  '河北': ['石家庄', '唐山', '秦皇岛', '邯郸', '邢台', '保定', '张家口', '承德', '沧州', '廊坊', '衡水'],
  '山西': ['太原', '大同', '阳泉', '长治', '晋城', '朔州', '晋中', '运城', '忻州', '临汾', '吕梁'],
  '辽宁': ['沈阳', '大连', '鞍山', '抚顺', '本溪', '丹东', '锦州', '营口', '阜新', '辽阳', '盘锦', '铁岭', '朝阳', '葫芦岛'],
  '吉林': ['长春', '吉林', '四平', '辽源', '通化', '白山', '松原', '白城', '延边'],
  '黑龙江': ['哈尔滨', '齐齐哈尔', '鸡西', '鹤岗', '双鸭山', '大庆', '伊春', '佳木斯', '七台河', '牡丹江', '黑河', '绥化', '大兴安岭'],
  '江苏': ['南京', '无锡', '徐州', '常州', '苏州', '南通', '连云港', '淮安', '盐城', '扬州', '镇江', '泰州', '宿迁'],
  '浙江': ['杭州', '宁波', '温州', '嘉兴', '湖州', '绍兴', '金华', '衢州', '舟山', '台州', '丽水'],
  '安徽': ['合肥', '芜湖', '蚌埠', '淮南', '马鞍山', '淮北', '铜陵', '安庆', '黄山', '滁州', '阜阳', '宿州', '六安', '亳州', '池州', '宣城'],
  '福建': ['福州', '厦门', '莆田', '三明', '泉州', '漳州', '南平', '龙岩', '宁德'],
  '江西': ['南昌', '景德镇', '萍乡', '九江', '新余', '鹰潭', '赣州', '吉安', '宜春', '抚州', '上饶'],
  '山东': ['济南', '青岛', '淄博', '枣庄', '东营', '烟台', '潍坊', '济宁', '泰安', '威海', '日照', '临沂', '德州', '聊城', '滨州', '菏泽'],
  '河南': ['郑州', '开封', '洛阳', '平顶山', '安阳', '鹤壁', '新乡', '焦作', '濮阳', '许昌', '漯河', '三门峡', '南阳', '商丘', '信阳', '周口', '驻马店'],
  '湖北': ['武汉', '黄石', '十堰', '宜昌', '襄阳', '鄂州', '荆门', '孝感', '荆州', '黄冈', '咸宁', '随州', '恩施'],
  '湖南': ['长沙', '株洲', '湘潭', '衡阳', '邵阳', '岳阳', '常德', '张家界', '益阳', '郴州', '永州', '怀化', '娄底', '湘西'],
  '广东': ['广州', '深圳', '珠海', '汕头', '佛山', '韶关', '湛江', '肇庆', '江门', '茂名', '惠州', '梅州', '汕尾', '河源', '阳江', '清远', '东莞', '中山', '潮州', '揭阳', '云浮'],
  '海南': ['海口', '三亚', '三沙', '儋州'],
  '四川': ['成都', '自贡', '攀枝花', '泸州', '德阳', '绵阳', '广元', '遂宁', '内江', '乐山', '南充', '眉山', '宜宾', '广安', '达州', '雅安', '巴中', '资阳', '阿坝', '甘孜', '凉山'],
  '贵州': ['贵阳', '六盘水', '遵义', '安顺', '毕节', '铜仁', '黔西南', '黔东南', '黔南'],
  '云南': ['昆明', '曲靖', '玉溪', '保山', '昭通', '丽江', '普洱', '临沧', '楚雄', '红河', '文山', '西双版纳', '大理', '德宏', '怒江', '迪庆'],
  '陕西': ['西安', '铜川', '宝鸡', '咸阳', '渭南', '延安', '汉中', '榆林', '安康', '商洛'],
  '甘肃': ['兰州', '嘉峪关', '金昌', '白银', '天水', '武威', '张掖', '平凉', '酒泉', '庆阳', '定西', '陇南', '临夏', '甘南'],
  '青海': ['西宁', '海东', '海北', '黄南', '海南', '果洛', '玉树', '海西'],
  '内蒙古': ['呼和浩特', '包头', '乌海', '赤峰', '通辽', '鄂尔多斯', '呼伦贝尔', '巴彦淖尔', '乌兰察布', '兴安', '锡林郭勒', '阿拉善'],
  '广西': ['南宁', '柳州', '桂林', '梧州', '北海', '防城港', '钦州', '贵港', '玉林', '百色', '贺州', '河池', '来宾', '崇左'],
  '西藏': ['拉萨', '日喀则', '昌都', '林芝', '山南', '那曲', '阿里'],
  '宁夏': ['银川', '石嘴山', '吴忠', '固原', '中卫'],
  '新疆': ['乌鲁木齐', '克拉玛依', '吐鲁番', '哈密', '昌吉', '博尔塔拉', '巴音郭楞', '阿克苏', '克孜勒苏', '喀什', '和田', '伊犁', '塔城', '阿勒泰'],
};

const unitTypes = [
  { value: 'school', label: '学校', icon: School },
  { value: 'institution', label: '机构', icon: Building2 },
  { value: 'individual', label: '个体', icon: UserCircle },
];

interface Teacher {
  id: string;
  username: string;
  phone: string;
  region: string;
  unit_type: 'school' | 'institution' | 'individual';
  unit_name: string;
  audit_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  reject_reason?: string;
  remark?: string;
  bind_code?: string;
}

interface TeacherPackage {
  id: string;
  organization: string;
  category: string;
  level_text: string;
  package_type: string;
  people_count: number;
  expire_date: string;
  price: number;
  is_active?: boolean;
  created_at?: string;
  package_source?: 'self' | 'admin';
  remark?: string;
  actual_price?: number;
  order_id?: string;
  used_quota?: number;
  [key: string]: any; // 允许额外字段以兼容数据库返回的数据
}

interface PackageFormData {
  orgId: string;
  categoryId: string;
  levelId: string;
  levelText: string;
  packageType: 'month' | 'quarter' | 'halfYear' | 'year' | 'custom';
  peopleCount: number;
  price: number;
  actualPrice: number;
  expireDate: string;
  customDays: number;
}

const unitTypeMap: Record<string, string> = {
  school: '学校',
  institution: '机构',
  individual: '个体'
};

const packageTypeMap: Record<string, string> = {
  month: '月包',
  quarter: '季包',
  halfYear: '半年包',
  year: '年包',
  custom: '自定义包'
};

export default function Teachers() {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [teacherPackages, setTeacherPackages] = useState<Record<string, TeacherPackage[]>>({});
  const [teacherPointsMap, setTeacherPointsMap] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; teacher?: Teacher; isBatch?: boolean }>({ open: false });
  const [packageModal, setPackageModal] = useState<{ open: boolean; teacher?: Teacher }>({ open: false });
  const [viewPackagesModal, setViewPackagesModal] = useState<{ open: boolean; teacher?: Teacher }>({ open: false });
  const [teacherDetailModal, setTeacherDetailModal] = useState<{ open: boolean; teacher?: Teacher }>({ open: false });
  const [showRefundDetailModal, setShowRefundDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [teacherAllPackages, setTeacherAllPackages] = useState<any[]>([]);
  const [isEditingTeacher, setIsEditingTeacher] = useState(false);
  const [editTeacherForm, setEditTeacherForm] = useState({
    username: '',
    phone: '',
    province: '',
    city: '',
    unit_type: 'school',
    unit_name: '',
    audit_status: 'pending'
  });
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});
  const [editAvailableCities, setEditAvailableCities] = useState<string[]>([]);
  const [closePackageModal, setClosePackageModal] = useState<{ open: boolean; teacher?: Teacher; packageId?: string }>({ open: false });
  const [remarkModal, setRemarkModal] = useState<{ open: boolean; packageItem?: TeacherPackage }>({ open: false });
  const [remarkInput, setRemarkInput] = useState('');
  const [resetPasswordModal, setResetPasswordModal] = useState<{ open: boolean; teacher?: Teacher }>({ open: false });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string }>({ type: '', text: '' });
  const [confirmPackageModal, setConfirmPackageModal] = useState<{ open: boolean }>({ open: false });
  const [packageForm, setPackageForm] = useState<PackageFormData>({
    orgId: '',
    categoryId: '',
    levelId: '',
    levelText: '',
    packageType: 'month',
    peopleCount: 0,
    price: 0,
    actualPrice: 0,
    expireDate: '',
    customDays: 30
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [institutionPrices, setInstitutionPrices] = useState<InstitutionPrice[]>([]);
  const [availableCategories, setAvailableCategories] = useState<CourseCategory[]>([]);
  const [availableLevels, setAvailableLevels] = useState<Level[]>([]);
  const [availablePrices, setAvailablePrices] = useState<InstitutionPrice[]>([]);
  const [addTeacherModal, setAddTeacherModal] = useState(false);
  const [newTeacherForm, setNewTeacherForm] = useState({
    username: '',
    phone: '',
    province: '',
    city: '',
    unit_type: 'school',
    unit_name: '',
    password: '',
    confirmPassword: ''
  });
  const [teacherRemark, setTeacherRemark] = useState('');
  const [isEditingRemark, setIsEditingRemark] = useState(false);
  const [isSavingRemark, setIsSavingRemark] = useState(false);
  const [teacherBindCode, setTeacherBindCode] = useState('');
  const [isEditingBindCode, setIsEditingBindCode] = useState(false);
  const [bindCodeInput, setBindCodeInput] = useState('');
  const [isSavingBindCode, setIsSavingBindCode] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const { hasPaidPapers, isChecking, error: validationError, checkPaidPapers, resetValidation } = usePaperValidation();

  // U币管理相关状态
  const [showPointModal, setShowPointModal] = useState(false);
  const [pointTeacher, setPointTeacher] = useState<Teacher | null>(null);
  const [pointBalance, setPointBalance] = useState<number>(0);
  const [pointLoading, setPointLoading] = useState(false);
  const [pointAction, setPointAction] = useState<'add' | 'deduct' | 'gift'>('add');
  const [pointAmount, setPointAmount] = useState<number>(0);
  const [pointRemark, setPointRemark] = useState('');
  const [pointSuccess, setPointSuccess] = useState(false);

  useEffect(() => {
    loadTeachers();
    loadCategoryData();

    const handleStudentDeleted = () => {
      loadTeachers();
    };
    window.addEventListener('student-deleted', handleStudentDeleted);

    return () => {
      window.removeEventListener('student-deleted', handleStudentDeleted);
    };
  }, []);

  const loadTeachers = async () => {
    // 只查询已通过审核的教师
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'teacher')
      .eq('audit_status', 'approved')
      .order('created_at', { ascending: false });
    if (error) {

      return;
    }
    if (data) {
      setTeachers(data as Teacher[]);

      // 批量查询所有教师的套餐，替代逐个查询
      const teacherIds = data.map(t => t.id);
      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
      const { data: allPackages } = await supabase
        .from('teacher_packages')
        .select('*')
        .in('teacher_id', teacherIds)
        .eq('is_active', true)
        .gte('expire_date', today)
        .order('created_at', { ascending: false });

      const packagesMap: Record<string, TeacherPackage[]> = {};
      if (allPackages) {
        for (const pkg of allPackages) {
          if (!packagesMap[pkg.teacher_id]) packagesMap[pkg.teacher_id] = [];
          packagesMap[pkg.teacher_id].push(pkg as TeacherPackage);
        }
      }
      // 确保没有套餐的教师也有空数组
      for (const teacher of data) {
        if (!packagesMap[teacher.id]) packagesMap[teacher.id] = [];
      }
      setTeacherPackages(packagesMap);

      // 批量获取教师U币余额
      const { data: pointAccounts } = await supabase
        .from('user_point_accounts')
        .select('user_id, total_points')
        .eq('role', 'teacher')
        .in('user_id', teacherIds);

      if (pointAccounts) {
        const pointsMap: Record<string, number> = {};
        pointAccounts.forEach(account => {
          pointsMap[account.user_id] = account.total_points || 0;
        });
        setTeacherPointsMap(pointsMap);
      }
    }
  };

  const loadCategoryData = async () => {
    await categoryStore.init();
    setOrganizations(categoryStore.getOrganizations());
    setCategories(categoryStore.getCategories());
    setLevels(categoryStore.getLevels());
    setInstitutionPrices(categoryStore.getInstitutionPrices());
  };

  const filteredTeachers = (teachers || []).filter(t => {
    return t.username.includes(searchQuery) || t.phone.includes(searchQuery);
  });

  const totalPages = Math.ceil(filteredTeachers.length / pageSize);
  const paginatedTeachers = filteredTeachers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedTeachers.map(t => t.id);
    const allSelected = pageIds.every(id => selectedIds.includes(id));
    setSelectedIds(prev => allSelected ? prev.filter(id => !pageIds.includes(id)) : [...prev, ...pageIds.filter(id => !prev.includes(id))]);
  };

  const handleDelete = (teacher: Teacher) => {
    setDeleteModal({ open: true, teacher, isBatch: false });
  };

  // U币管理函数
  const openPointModal = async (teacher: Teacher) => {
    setPointTeacher(teacher);
    setPointAction('add');
    setPointAmount(0);
    setPointRemark('');
    setPointSuccess(false);
    setShowPointModal(true);
    // 查询当前U币余额
    try {
      const { data: account } = await supabase
        .from('user_point_accounts')
        .select('total_points')
        .eq('user_id', teacher.id)
        .eq('role', 'teacher')
        .maybeSingle();
      setPointBalance(account?.total_points || 0);
    } catch {
      setPointBalance(0);
    }
  };

  const handlePointAction = async () => {
    if (!pointTeacher || pointAmount <= 0) {
      alert('请输入有效的U币数量');
      return;
    }

    setPointLoading(true);
    try {
      const adminUser = (await supabase.auth.getUser()).data.user;

      // 先确保教师有U币账户
      const { data: existingAccount } = await supabase
        .from('user_point_accounts')
        .select('id')
        .eq('user_id', pointTeacher.id)
        .eq('role', 'teacher')
        .maybeSingle();

      if (!existingAccount) {
        // 创建U币账户
        const createResponse = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'create_account',
            user_id: pointTeacher.id,
            role: 'teacher',
          }),
        });
        const createResult = await createResponse.json();
        if (!createResult.success) {
          alert(`创建U币账户失败: ${createResult.error || '未知错误'}`);
          setPointLoading(false);
          return;
        }
      }

      let action: string;
      let sourceType: string;
      let pointType: string;

      switch (pointAction) {
        case 'add':
          action = 'add_points';
          sourceType = 'admin_adjust';
          pointType = 'activity_reward';
          break;
        case 'deduct':
          action = 'deduct_points';
          sourceType = 'admin_adjust';
          pointType = 'activity_reward';
          break;
        case 'gift':
          action = 'add_points';
          sourceType = 'admin_gift';
          pointType = 'teacher_gift';
          break;
        default:
          action = 'add_points';
          sourceType = 'admin_adjust';
          pointType = 'activity_reward';
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          user_id: pointTeacher.id,
          role: 'teacher',
          points: pointAmount,
          point_type: pointType,
          source_type: sourceType,
          source_detail: pointRemark || `${pointAction === 'add' ? '管理员增加U币' : pointAction === 'deduct' ? '管理员扣减U币' : '管理员赠送U币'} ${pointAmount}`,
          operator_id: adminUser?.id,
          operator_role: 'admin',
          remark: pointRemark,
        }),
      });

      const result = await response.json();
      if (result.success || result.new_balance !== undefined) {
        setPointSuccess(true);
        // 更新余额显示
        const { data: account } = await supabase
          .from('user_point_accounts')
          .select('total_points')
          .eq('user_id', pointTeacher.id)
          .eq('role', 'teacher')
          .maybeSingle();
        setPointBalance(account?.total_points || 0);
        // 记录操作日志
        try {
          await supabase.from('admin_operation_logs').insert({
            admin_id: adminUser?.id || '',
            admin_name: (() => { try { const s = JSON.parse(localStorage.getItem('admin_session') || '{}'); return s.username || '管理员'; } catch { return '管理员'; } })(),
            admin_role: (() => { try { const s = JSON.parse(localStorage.getItem('admin_session') || '{}'); return s.role === 'super' ? 'super' : 'admin'; } catch { return 'admin'; } })(),
            operation_type: 'point_adjust',
            target_user_id: pointTeacher.id,
            target_user_name: pointTeacher.username,
            details: { action: pointAction, amount: pointAmount, remark: pointRemark, role: 'teacher' },
            operation_desc: `教师${pointAction === 'add' ? '增加' : pointAction === 'deduct' ? '扣减' : '赠送'}U币 ${pointAmount}，备注：${pointRemark || '无'}`,
          });
        } catch (logError) {
          console.error('记录操作日志失败:', logError);
        }
      } else {
        alert(`操作失败: ${result.error || '未知错误'}`);
      }
    } catch (error) {
      console.error('U币操作失败:', error);
      alert('操作失败，请稍后重试');
    } finally {
      setPointLoading(false);
    }
  };

  const handleBatchDelete = () => {
    if (selectedIds.length === 0) return;
    setDeleteModal({ open: true, isBatch: true });
  };

  const confirmDelete = async () => {
    try {
      const idsToDelete = deleteModal.isBatch ? selectedIds : (deleteModal.teacher ? [deleteModal.teacher.id] : []);
      if (deleteModal.isBatch) {
        for (const id of selectedIds) {
          await cascadeDeleteTeacher(id);
        }
      } else if (deleteModal.teacher) {
        await cascadeDeleteTeacher(deleteModal.teacher.id);
      }
      // 局部更新：从列表中移除已删除的教师，清理对应套餐
      setTeachers(prev => prev.filter(t => !idsToDelete.includes(t.id)));
      setTeacherPackages(prev => {
        const next = { ...prev };
        idsToDelete.forEach(id => delete next[id]);
        return next;
      });
      setDeleteModal({ open: false });
      setSelectedIds([]);
    } catch (error: any) {

      alert('删除失败: ' + (error.message || '请稍后重试'));
    }
  };

  const cascadeDeleteTeacher = async (teacherId: string) => {
    try {
      const { data: teacher } = await supabase
        .from('profiles')
        .select('username, phone')
        .eq('id', teacherId)
        .single();

      const { data: teacherPackages } = await supabase
        .from('teacher_packages')
        .select('id, organization, category, level_text')
        .eq('teacher_id', teacherId)
        .eq('is_active', true);

      if (teacherPackages && teacherPackages.length > 0) {
        await supabase
          .from('teacher_packages')
          .update({ is_active: false })
          .eq('teacher_id', teacherId);
      }

      // 删除教师-学员绑定关系
      await supabase
        .from('teacher_students')
        .delete()
        .eq('teacher_id', teacherId);

      // 删除教师账号（user_accounts）
      await supabase
        .from('user_accounts')
        .delete()
        .eq('auth_user_id', teacherId)
        .eq('role', 'teacher');

      // 将教师资料标记为已删除（profiles 有外键约束，不能直接删除）
      await supabase
        .from('profiles')
        .update({ role: 'deleted' })
        .eq('id', teacherId);

      // 获取当前操作员信息（管理员可能无有效session）
      const { data: userData } = await supabase.auth.getUser();
      const operatorId = userData.user?.id || '00000000-0000-0000-0000-000000000000';

      // 插入操作日志（跳过必填字段检查，使用默认值）
      await supabase.from('admin_operation_logs').insert({
        admin_id: operatorId,
        admin_name: (() => { try { const s = JSON.parse(localStorage.getItem('admin_session') || '{}'); return s.username || '管理员'; } catch { return '管理员'; } })(),
        admin_role: (() => { try { const s = JSON.parse(localStorage.getItem('admin_session') || '{}'); return s.role === 'super' ? 'super' : 'admin'; } catch { return 'admin'; } })(),
        operation_type: 'delete_teacher',
        operation_desc: `删除教师账号：${teacher?.username || ''}(${teacher?.phone || ''})，级联关闭了${teacherPackages?.length || 0}个教师刷题包`,
        target_user_id: teacherId,
        target_user_name: teacher?.username,
        details: {
          teacher_id: teacherId,
          teacher_name: teacher?.username,
          teacher_phone: teacher?.phone,
          closed_teacher_packages: teacherPackages?.length || 0
        }
      });

      window.dispatchEvent(new CustomEvent('teacher-deleted', { detail: { teacherId } }));
    } catch (error) {
      
      throw error;
    }
  };

  const getExpireDateByPackageType = (packageType: string, customDays?: number): string => {
    const today = new Date();
    const expireDate = new Date(today);
    let days = 30;
    switch (packageType) {
      case 'month': days = 30; break;
      case 'quarter': days = 90; break;
      case 'halfYear': days = 180; break;
      case 'year': days = 365; break;
      case 'custom': days = customDays || 30; break;
      default: days = 30;
    }
    expireDate.setDate(today.getDate() + days);
    return `${expireDate.getFullYear()}-${String(expireDate.getMonth() + 1).padStart(2, '0')}-${String(expireDate.getDate()).padStart(2, '0')}`;
  };

  const handleOpenPackage = (teacher: Teacher) => {
    setPackageModal({ open: true, teacher });
    setPackageForm({
      orgId: '',
      categoryId: '',
      levelId: '',
      levelText: '',
      packageType: 'month',
      peopleCount: 0,
      price: 0,
      actualPrice: 0,
      expireDate: '',
      customDays: 30
    });
    setAvailableCategories([]);
    setAvailableLevels([]);
    setAvailablePrices([]);
    resetValidation();
  };

  const handleOrgChange = (orgId: string) => {
    setPackageForm(prev => ({ ...prev, orgId, categoryId: '', levelId: '', peopleCount: 10, price: 0, actualPrice: 0 }));
    const cats = (categories || []).filter(c => c.orgId === orgId);
    setAvailableCategories(cats);
    setAvailableLevels([]);
    setAvailablePrices([]);
    resetValidation();
  };

  const handleCategoryChange = (categoryId: string) => {
    setPackageForm(prev => ({ ...prev, categoryId, levelId: '', peopleCount: 10, price: 0, actualPrice: 0 }));
    const lvls = (levels || []).filter(l => l.catId === categoryId);
    setAvailableLevels(lvls);
    setAvailablePrices([]);
    resetValidation();
  };

  const handleLevelChange = (levelId: string) => {
    const level = levels.find(l => l.id === levelId);
    setPackageForm(prev => ({ ...prev, levelId, levelText: level?.name || '', peopleCount: 10, price: 0, actualPrice: 0 }));
    const prices = institutionPrices.filter(p => p.levelId === levelId);
    setAvailablePrices(prices);

    // 触发试卷校验
    const org = organizations.find(o => o.id === packageForm.orgId);
    const cat = categories.find(c => c.id === packageForm.categoryId);
    const lvl = levels.find(l => l.id === levelId);
    if (org?.name && cat?.name && lvl?.name) {
      checkPaidPapers(org.name, cat.name, lvl.name);
    }
  };

  const handlePackageTypeChange = (packageType: 'month' | 'quarter' | 'halfYear' | 'year' | 'custom') => {
    const price = getPriceForPackageType(packageType);
    const customDays = packageType === 'custom' ? packageForm.customDays : undefined;
    const expireDate = getExpireDateByPackageType(packageType, customDays);
    setPackageForm(prev => ({ ...prev, packageType, price, actualPrice: price, expireDate }));
  };

  const handlePeopleCountChange = (peopleCount: number) => {
    const price = getPriceForPackageType(packageForm.packageType, peopleCount);
    setPackageForm(prev => ({ ...prev, peopleCount, price, actualPrice: price }));
  };

  const getPriceForPackageType = (packageType: string, peopleCount?: number): number => {
    const count = peopleCount || packageForm.peopleCount;
    const price = availablePrices.find(p => p.peopleCount === count);
    if (!price) return 0;
    switch (packageType) {
      case 'month': return price.priceMonth || 0;
      case 'quarter': return price.priceQuarter || 0;
      case 'halfYear': return price.priceHalfYear || 0;
      case 'year': return price.priceYear || 0;
      default: return 0;
    }
  };

  const confirmOpenPackage = async () => {
    if (!packageModal.teacher) return;
    if (!packageForm.orgId || !packageForm.categoryId || !packageForm.levelId) {
      alert('请选择考评体系、课程分类和级别');
      return;
    }
    if (!packageForm.expireDate) {
      alert('请选择套餐类型');
      return;
    }
    const org = organizations.find(o => o.id === packageForm.orgId);
    const cat = categories.find(c => c.id === packageForm.categoryId);
    const lvl = levels.find(l => l.id === packageForm.levelId);

    // 先创建套餐记录（教师套餐不需要人数字段）
    const { data: packageData, error: packageError } = await supabase.from('teacher_packages').insert({
      teacher_id: packageModal.teacher.id,
      organization: org?.name || '',
      category: cat?.name || '',
      level_text: lvl?.name || '',
      package_type: packageForm.packageType,
      expire_date: packageForm.expireDate,
      price: packageForm.price || null,
      actual_price: packageForm.actualPrice || null,
      package_source: 'admin'
    }).select();

    if (packageError) {
      
      alert('开通失败: ' + packageError.message);
      return;
    }

    const newPackageId = packageData?.[0]?.id;
    if (!newPackageId) {
      alert('开通失败：无法获取套餐ID');
      return;
    }

    // 生成订单号
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const orderNo = `T${timestamp}${random}`;

    // 创建订单记录
    const { data: orderData, error: orderError } = await supabase.from('teacher_orders').insert({
      teacher_id: packageModal.teacher.id,
      package_id: newPackageId,
      order_no: orderNo,
      original_price: packageForm.price,
      actual_price: packageForm.actualPrice,
      status: 'paid',
      pay_method: 'admin',
      package_source: 'admin',
      paid_at: new Date().toISOString(),
      expire_date: packageForm.expireDate
    }).select();

    if (orderError) {
      
      alert('套餐开通成功，但订单生成失败，请联系管理员处理');
    } else {
      // 更新套餐的 order_id
      const newOrderId = orderData?.[0]?.id;
      if (newOrderId) {
        await supabase.from('teacher_packages').update({ order_id: newOrderId }).eq('id', newPackageId);
      }
    }

    // 局部更新：将新套餐添加到 teacherPackages 中对应教师的列表
    if (packageData && packageData[0]) {
      const newPkg = packageData[0] as TeacherPackage;
      // 如果有 order_id，更新到套餐中
      if (orderData && orderData[0]) {
        newPkg.order_id = orderData[0].id;
      }
      setTeacherPackages(prev => ({
        ...prev,
        [packageModal.teacher.id]: [...(prev[packageModal.teacher.id] || []), newPkg]
      }));
    }
    setConfirmPackageModal({ open: false });
    setPackageModal({ open: false });
  };

  const handleViewPackages = (teacher: Teacher) => {
    setViewPackagesModal({ open: true, teacher });
  };

  const handleViewTeacherDetail = async (teacher: Teacher) => {
    setTeacherDetailModal({ open: true, teacher });
    setIsEditingTeacher(false);
    const regionParts = teacher.region?.split('-') || ['', ''];
    const province = regionParts[0] || '';
    const city = regionParts[1] || '';
    setEditTeacherForm({
      username: teacher.username,
      phone: teacher.phone?.includes('@') ? teacher.phone.split('@')[0] : teacher.phone,
      province: province,
      city: city,
      unit_type: teacher.unit_type,
      unit_name: teacher.unit_name,
      audit_status: teacher.audit_status
    });
    if (province && regionData[province]) {
      setEditAvailableCities(regionData[province]);
    } else {
      setEditAvailableCities([]);
    }
    setEditFormErrors({});
    const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
    const { data: packages } = await supabase
      .from('teacher_packages')
      .select('*')
      .eq('teacher_id', teacher.id)
      .order('created_at', { ascending: false });
    setTeacherAllPackages(packages || []);

    const { data: activePackages } = await supabase
      .from('teacher_packages')
      .select('*')
      .eq('teacher_id', teacher.id)
      .eq('is_active', true)
      .gte('expire_date', today)
      .order('created_at', { ascending: false });
    setTeacherPackages(prev => ({ ...prev, [teacher.id]: (activePackages || []) as TeacherPackage[] }));
    setTeacherRemark(teacher.remark || '');
    setTeacherBindCode(teacher.bind_code || '');
    setIsEditingBindCode(false);
    setBindCodeInput('');
  };

  const handleEditProvinceChange = (province: string) => {
    setEditTeacherForm(prev => ({ ...prev, province, city: '' }));
    setEditAvailableCities(regionData[province] || []);
  };

  const validateEditForm = () => {
    const errors: Record<string, string> = {};
    if (!editTeacherForm.username.trim()) errors.username = '请输入姓名';
    if (!editTeacherForm.phone.trim()) {
      errors.phone = '请输入手机号';
    } else if (!/^1[3-9]\d{9}$/.test(editTeacherForm.phone)) {
      errors.phone = '手机号格式不正确';
    }
    if (!editTeacherForm.province) errors.province = '请选择省份';
    if (!editTeacherForm.city) errors.city = '请选择城市';
    if (!editTeacherForm.unit_name.trim()) errors.unit_name = '请输入单位名称';
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTeacherEdit = async () => {
    if (!validateEditForm() || !teacherDetailModal.teacher) return;
    const region = `${editTeacherForm.province}-${editTeacherForm.city}`;
    const { error } = await supabase
      .from('profiles')
      .update({
        username: editTeacherForm.username,
        phone: editTeacherForm.phone.includes('@') ? editTeacherForm.phone : `${editTeacherForm.phone}@teacher.meoo.local`,
        region,
        unit_type: editTeacherForm.unit_type,
        unit_name: editTeacherForm.unit_name,
        audit_status: editTeacherForm.audit_status
      })
      .eq('id', teacherDetailModal.teacher.id);
    if (error) {
      setMessage({ type: 'error', text: '保存失败: ' + error.message });
    } else {
      // 同步更新 user_accounts 表的 username，确保 Header 能读到最新值
      await supabase
        .from('user_accounts')
        .update({ username: editTeacherForm.username })
        .eq('auth_user_id', teacherDetailModal.teacher.id);

      setMessage({ type: 'success', text: '保存成功' });
      setIsEditingTeacher(false);
      const updatedTeacher = { ...teacherDetailModal.teacher, username: editTeacherForm.username, phone: editTeacherForm.phone.includes('@') ? editTeacherForm.phone : `${editTeacherForm.phone}@teacher.meoo.local`, region, unit_type: editTeacherForm.unit_type as 'school' | 'institution' | 'individual', unit_name: editTeacherForm.unit_name, audit_status: editTeacherForm.audit_status as 'pending' | 'approved' | 'rejected' };
      setTeacherDetailModal({ open: true, teacher: updatedTeacher });
      // 局部更新：直接更新 teachers 列表中对应教师的信息
      setTeachers(prev => prev.map(t => t.id === teacherDetailModal.teacher!.id ? { ...t, username: editTeacherForm.username, phone: editTeacherForm.phone.includes('@') ? editTeacherForm.phone : `${editTeacherForm.phone}@teacher.meoo.local`, region, unit_type: editTeacherForm.unit_type as 'school' | 'institution' | 'individual', unit_name: editTeacherForm.unit_name, audit_status: editTeacherForm.audit_status as 'pending' | 'approved' | 'rejected' } : t));
      // 触发全局事件通知 Header 刷新用户信息
      window.dispatchEvent(new Event('user-info-updated'));
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const openRemarkModal = (pkg: TeacherPackage) => {
    setRemarkModal({ open: true, packageItem: pkg });
    setRemarkInput(pkg.remark || '');
  };

  const handleSaveTeacherRemark = async () => {
    if (!teacherDetailModal.teacher) return;
    setIsSavingRemark(true);
    const { error } = await supabase
      .from('profiles')
      .update({ remark: teacherRemark.trim() || null })
      .eq('id', teacherDetailModal.teacher.id);
    if (error) {
      setMessage({ type: 'error', text: '保存备注失败: ' + error.message });
    } else {
      setMessage({ type: 'success', text: '备注已更新' });
      const updatedTeacher = { ...teacherDetailModal.teacher, remark: teacherRemark.trim() || null };
      setTeacherDetailModal({ open: true, teacher: updatedTeacher });
      setIsEditingRemark(false);
    }
    setIsSavingRemark(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSaveRemark = async () => {
    if (!remarkModal.packageItem) return;
    const { error } = await supabase
      .from('teacher_packages')
      .update({ remark: remarkInput.trim() || null })
      .eq('id', remarkModal.packageItem.id);
    if (error) {
      setMessage({ type: 'error', text: '保存备注失败: ' + error.message });
    } else {
      setMessage({ type: 'success', text: '备注已保存' });
      setRemarkModal({ open: false });
      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
      const { data: packages } = await supabase
        .from('teacher_packages')
        .select('*')
        .eq('teacher_id', remarkModal.packageItem.id)
        .eq('is_active', true)
        .gte('expire_date', today)
        .order('created_at', { ascending: false });
      setTeacherAllPackages(packages || []);
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const confirmClosePackage = async () => {
    if (!closePackageModal.teacher || !closePackageModal.packageId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/package-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'close',
          packageId: closePackageModal.packageId,
          role: 'teacher',
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setMessage({ type: 'error', text: result.error || '关闭套餐失败' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }

      const teacherId = closePackageModal.teacher.id;
      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

      // 刷新可用套餐列表（排除已关闭的）
      const { data: refreshedPackages } = await supabase
        .from('teacher_packages')
        .select('*')
        .eq('teacher_id', teacherId)
        .eq('is_active', true)
        .gte('expire_date', today)
        .order('created_at', { ascending: false });

      const packagesList = refreshedPackages || [];
      setTeacherPackages(prev => ({ ...prev, [teacherId]: packagesList as TeacherPackage[] }));

      // 如果当前打开的是该教师的详情弹窗，同步刷新详情中的套餐数据
      if (teacherDetailModal.teacher?.id === teacherId) {
        const { data: allPackages } = await supabase
          .from('teacher_packages')
          .select('*')
          .eq('teacher_id', teacherId)
          .order('created_at', { ascending: false });
        setTeacherAllPackages(allPackages || []);
      }

      setClosePackageModal({ open: false });
      setMessage({ type: 'success', text: '套餐已关闭' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: '关闭套餐失败：' + error.message });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleProvinceChange = (province: string) => {
    setNewTeacherForm(prev => ({ ...prev, province, city: '' }));
    setAvailableCities(regionData[province] || []);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!newTeacherForm.username.trim()) errors.username = '请输入姓名';
    if (!newTeacherForm.phone.trim()) {
      errors.phone = '请输入手机号';
    } else if (!/^1[3-9]\d{9}$/.test(newTeacherForm.phone)) {
      errors.phone = '手机号格式不正确';
    }
    if (!newTeacherForm.province) errors.province = '请选择省份';
    if (!newTeacherForm.city) errors.city = '请选择城市';
    if (!newTeacherForm.unit_name.trim()) errors.unit_name = '请输入单位名称';
    if (!newTeacherForm.password) {
      errors.password = '请输入密码';
    } else if (newTeacherForm.password.length < 6) {
      errors.password = '密码至少6位';
    }
    if (newTeacherForm.password !== newTeacherForm.confirmPassword) {
      errors.confirmPassword = '两次密码不一致';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateBindCode = () => {
    return Math.floor(100 + Math.random() * 900).toString();
  };

  const handleAddTeacher = async () => {
    if (!validateForm()) return;

    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id, role')
      .eq('phone', newTeacherForm.phone)
      .maybeSingle();

    if (existingProfile) {
      if (existingProfile.role === 'student') {
        setFormErrors({ phone: '该手机号已注册为学员，不能注册为教师' });
        return;
      }
      if (existingProfile.role === 'teacher') {
        setFormErrors({ phone: '该手机号已注册为教师' });
        return;
      }
    }

    const bindCode = generateBindCode();
    const region = `${newTeacherForm.province}-${newTeacherForm.city}`;

    try {
      const email = `${newTeacherForm.phone}@teacher.meoo.local`;

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password: newTeacherForm.password,
        options: {
          data: {
            username: newTeacherForm.username,
            mobile: newTeacherForm.phone,
            role: 'teacher'
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered') || authError.message.includes('User already registered')) {
          setFormErrors({ phone: '该手机号已注册，请使用其他手机号' });
          return;
        }
        
        setFormErrors({ phone: authError.message });
        return;
      }

      if (authData.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            username: newTeacherForm.username,
            phone: newTeacherForm.phone,
            region,
            unit_type: newTeacherForm.unit_type,
            unit_name: newTeacherForm.unit_name,
            role: 'teacher',
            audit_status: 'approved',
            bind_code: bindCode
          })
          .eq('id', authData.user.id);

        if (profileError) {
          
          setFormErrors({ phone: profileError.message });
          return;
        }

        // 创建 user_accounts 业务记录（密码由 Supabase Auth 管理）
        const { error: accountError } = await supabase
          .from('user_accounts')
          .insert({
            mobile: newTeacherForm.phone,
            role: 'teacher',
            status: 'approved',
            auth_user_id: authData.user.id,
            username: newTeacherForm.username
          });

        if (accountError) {
          
          setFormErrors({ phone: accountError.message });
          return;
        }
      }

      await fetch('/functions/v1/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: newTeacherForm.phone,
          templateCode: 'SMS_506060098',
          params: { name: newTeacherForm.username, code: bindCode }
        })
      });

      setAddTeacherModal(false);
      setNewTeacherForm({
        username: '',
        phone: '',
        province: '',
        city: '',
        unit_type: 'school',
        unit_name: '',
        password: '',
        confirmPassword: ''
      });
      setFormErrors({});
      // 局部更新：将新教师添加到列表头部
      if (authData.user) {
        const newTeacher: Teacher = {
          id: authData.user.id,
          username: newTeacherForm.username,
          phone: `${newTeacherForm.phone}@teacher.meoo.local`,
          region: `${newTeacherForm.province}-${newTeacherForm.city}`,
          unit_type: newTeacherForm.unit_type as 'school' | 'institution' | 'individual',
          unit_name: newTeacherForm.unit_name,
          audit_status: 'approved',
          created_at: new Date().toISOString(),
          bind_code: bindCode,
        };
        setTeachers(prev => [newTeacher, ...prev]);
        setTeacherPackages(prev => ({ ...prev, [authData.user!.id]: [] }));
      }
    } catch (error: any) {
      
      setFormErrors({ phone: error.message || '添加失败' });
    }
  };

  const formatPackages = (packages: TeacherPackage[]) => {
    if (!packages || packages.length === 0) return '-';
    const now = new Date();
    const availablePackages = (packages || []).filter(p => p.is_active !== false && new Date(p.expire_date) >= now);
    if (availablePackages.length === 0) return '-';
    return availablePackages.map(p =>
      `${p.organization}-${p.category}-${p.level_text} ${packageTypeMap[p.package_type]} 到期:${p.expire_date}`
    ).join('; ');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">教师列表</h1>
        <p className="text-gray-500 mt-1">管理教师账号及审核状态</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="搜索姓名或手机号" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <button onClick={() => setAddTeacherModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">
            <Plus className="w-4 h-4" />添加教师
          </button>
          {selectedIds.length > 0 && (
            <div className="flex gap-2">
              <button onClick={handleBatchDelete} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm hover:bg-gray-100">
                <Trash2 className="w-4 h-4" />批量删除
              </button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input type="checkbox" checked={paginatedTeachers.length > 0 && paginatedTeachers.every(t => selectedIds.includes(t.id))} onChange={toggleSelectAll} className="rounded" />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">姓名</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">手机号</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">所属地区</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">单位性质</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">单位名称</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">账号状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">U币余额</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">注册时间</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">可用套餐</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTeachers.map(teacher => (
                <motion.tr key={teacher.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selectedIds.includes(teacher.id)} onChange={() => toggleSelect(teacher.id)} className="rounded" />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">
                    <button onClick={() => handleViewTeacherDetail(teacher)} className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
                      {teacher.username}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <span>{teacher.phone?.includes('@') ? teacher.phone.split('@')[0] : teacher.phone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span>{teacher.region}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{unitTypeMap[teacher.unit_type]}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      <span>{teacher.unit_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600">
                      已通过
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/admin/point-transactions?userId=${teacher.id}&role=teacher&returnUrl=/admin/teachers`)}
                      className="text-orange-600 hover:text-orange-700 font-medium cursor-pointer hover:underline"
                    >
                      {teacherPointsMap[teacher.id] || 0}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{(() => {
                        try {
                          const date = new Date(teacher.created_at);
                          if (isNaN(date.getTime())) return '-';
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${year}-${month}-${day} ${hours}:${minutes}`;
                        } catch {
                          return '-';
                        }
                      })()}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div className="flex flex-col gap-1">
                      {(() => {
                        const packages = teacherPackages[teacher.id];
                        if (!packages || packages.length === 0) return <span className="text-gray-400">-</span>;
                        const now = new Date();
                        const availablePackages = (packages || []).filter(p => p.is_active !== false && new Date(p.expire_date) >= now);
                        if (availablePackages.length === 0) return <span className="text-gray-400">-</span>;
                        return availablePackages.map((p) => (
                          <div key={p.id} className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-gray-700">{p.organization}-{p.category}-{p.level_text} {packageTypeMap[p.package_type]}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleOpenPackage(teacher)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="开通刷题包"><Package className="w-4 h-4" /></button>
                      <button onClick={() => openPointModal(teacher)} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded" title="U币管理"><Crown className="w-4 h-4" /></button>
                      <button onClick={() => setResetPasswordModal({ open: true, teacher })} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="重置密码"><Key className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(teacher)} className="p-1.5 text-gray-600 hover:bg-gray-50 rounded" title="删除"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">共 {filteredTeachers.length} 条记录</span>
            <div className="flex gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50">上一页</button>
              <span className="px-3 py-1 text-sm text-gray-600">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50">下一页</button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {deleteModal.open && (
          <AuthConfirmModal
            isOpen={deleteModal.open}
            title="确认删除教师"
            message={deleteModal.isBatch
              ? `确定要删除选中的 ${selectedIds.length} 个教师账号吗？此操作不可恢复，相关的刷题包也将被关闭。`
              : `确定要删除教师 "${deleteModal.teacher?.username}" 吗？此操作不可恢复，相关的刷题包也将被关闭。`}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteModal({ open: false })}
          />
        )}
      </AnimatePresence>

      {/* 使用通用开通套餐组件 */}
      <PackageGrantModal
        showPackageModal={packageModal.open}
        setShowPackageModal={(show) => setPackageModal({ open: show, teacher: packageModal.teacher })}
        showConfirmPackageModal={confirmPackageModal.open}
        setShowConfirmPackageModal={(show) => setConfirmPackageModal({ open: show })}
        userName={packageModal.teacher?.username || ''}
        userLabel="教师姓名"
        packageForm={packageForm}
        setPackageForm={setPackageForm}
        orgs={organizations}
        availableCategories={availableCategories}
        availableLevels={availableLevels}
        handleOrgChange={handleOrgChange}
        handleCategoryChange={handleCategoryChange}
        handleLevelChange={handleLevelChange}
        onPackageTypeChange={handlePackageTypeChange}
        isChecking={isChecking}
        hasPaidPapers={hasPaidPapers}
        validationError={validationError}
        onConfirm={confirmOpenPackage}
      />

      <AnimatePresence>
        {viewPackagesModal.open && viewPackagesModal.teacher && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-xl p-6 w-[900px] max-h-[80vh] overflow-auto">
              <h3 className="text-lg font-bold text-gray-800 mb-4">{viewPackagesModal.teacher.username} - 已购套餐</h3>
              {teacherPackages[viewPackagesModal.teacher.id]?.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">考评体系</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">课程分类</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">级别分类</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">套餐类型</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">购买时间</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">到期时间</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">状态</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {teacherPackages[viewPackagesModal.teacher.id]
                      .slice()
                      .sort((a, b) => {
                        const now = new Date();
                        const getStatusPriority = (pkg: TeacherPackage) => {
                          if (pkg.is_active === false) return 2;
                          if (new Date(pkg.expire_date) < now) return 1;
                          return 0;
                        };
                        const priorityA = getStatusPriority(a);
                        const priorityB = getStatusPriority(b);
                        if (priorityA !== priorityB) {
                          return priorityA - priorityB;
                        }
                        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
                      })
                      .map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-800">{pkg.organization}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{pkg.category}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{pkg.level_text}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{packageTypeMap[pkg.package_type]}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{pkg.created_at ? pkg.created_at.split('T')[0] : '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{pkg.expire_date}</td>
                        <td className="px-4 py-2 text-sm">
                          {pkg.is_active === false ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              已关闭
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-600">
                              生效中
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => {
                              if (pkg.is_active !== false) {
                                setClosePackageModal({ open: true, teacher: viewPackagesModal.teacher, packageId: pkg.id });
                              }
                            }}
                            disabled={pkg.is_active === false}
                            className={`px-2 py-1 text-xs rounded transition-colors ${pkg.is_active === false ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                          >
                            {pkg.is_active === false ? '已关闭' : '关闭'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-gray-400 py-8">暂无套餐</p>
              )}
              <div className="flex justify-end mt-4">
                <button onClick={() => setViewPackagesModal({ open: false })} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">关闭</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {resetPasswordModal.open && resetPasswordModal.teacher && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-xl p-6 w-[400px]">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-600" />
                重置密码
              </h3>
              <div className="space-y-3 mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">教师：</span>{resetPasswordModal.teacher.username}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">手机号：</span>{resetPasswordModal.teacher.phone?.includes('@') ? resetPasswordModal.teacher.phone.split('@')[0] : resetPasswordModal.teacher.phone}
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 text-sm">
                    确定要将该教师的密码重置为 <span className="font-bold">123456</span> 吗？
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setResetPasswordModal({ open: false })} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm">取消</button>
                <button
                  onClick={async () => {
                    setResetPasswordLoading(true);
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${session?.access_token}`,
                        },
                        body: JSON.stringify({ action: 'reset_password', teacherId: resetPasswordModal.teacher!.id }),
                      });
                      const result = await response.json();
                      if (!response.ok) throw new Error(result.error || '重置失败');
                      setMessage({ type: 'success', text: '密码重置成功' });
                      setResetPasswordModal({ open: false });
                    } catch (err: any) {
                      setMessage({ type: 'error', text: '密码重置失败: ' + err.message });
                    }
                    setResetPasswordLoading(false);
                    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                  }}
                  disabled={resetPasswordLoading}
                  className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50"
                >
                  {resetPasswordLoading ? '重置中...' : '确认重置'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {closePackageModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setClosePackageModal({ open: false })}>
          <div className="bg-white rounded-xl p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">确认关闭套餐</h3>
            <p className="text-sm text-gray-600 mb-4">
              确定要关闭该刷题包吗？关闭后该刷题包将不再可用，此操作不可恢复。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setClosePackageModal({ open: false })} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors">取消</button>
              <button onClick={confirmClosePackage} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors">确认关闭</button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 z-[60] px-6 py-3 rounded-lg shadow-lg ${
              message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {teacherDetailModal.open && teacherDetailModal.teacher && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-xl p-6 w-[1200px] max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">{teacherDetailModal.teacher.username} - 教师详情</h3>
                <button onClick={() => setTeacherDetailModal({ open: false })} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><UserCircle className="w-4 h-4" />基本信息</h4>
                    {!isEditingTeacher && (
                      <button onClick={() => {
                        if (teacherDetailModal.teacher) {
                          const regionParts = teacherDetailModal.teacher.region?.split('-') || ['', ''];
                          const province = regionParts[0] || '';
                          const city = regionParts[1] || '';
                          setEditTeacherForm({
                            username: teacherDetailModal.teacher.username,
                            phone: teacherDetailModal.teacher.phone?.includes('@') ? teacherDetailModal.teacher.phone.split('@')[0] : teacherDetailModal.teacher.phone,
                            province: province,
                            city: city,
                            unit_type: teacherDetailModal.teacher.unit_type,
                            unit_name: teacherDetailModal.teacher.unit_name,
                            audit_status: teacherDetailModal.teacher.audit_status
                          });
                          if (province && regionData[province]) {
                            setEditAvailableCities(regionData[province]);
                          } else {
                            setEditAvailableCities([]);
                          }
                          setEditFormErrors({});
                        }
                        setIsEditingTeacher(true);
                      }} className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-colors">
                        <Edit className="w-3 h-3" />修改
                      </button>
                    )}
                  </div>
                  {!isEditingTeacher ? (
                    <div className="grid grid-cols-4 gap-4">
                      <div><span className="text-gray-500 text-sm">姓名</span><p className="font-medium">{teacherDetailModal.teacher.username}</p></div>
                      <div><span className="text-gray-500 text-sm">手机号</span><p className="font-medium">{teacherDetailModal.teacher.phone?.includes('@') ? teacherDetailModal.teacher.phone.split('@')[0] : teacherDetailModal.teacher.phone}</p></div>
                      <div><span className="text-gray-500 text-sm">所属地区</span><p className="font-medium">{teacherDetailModal.teacher.region}</p></div>
                      <div><span className="text-gray-500 text-sm">单位性质</span><p className="font-medium">{unitTypeMap[teacherDetailModal.teacher.unit_type]}</p></div>
                      <div><span className="text-gray-500 text-sm">单位名称</span><p className="font-medium">{teacherDetailModal.teacher.unit_name}</p></div>
                      <div><span className="text-gray-500 text-sm">注册时间</span><p className="font-medium">{formatDateTime(teacherDetailModal.teacher.created_at)}</p></div>
                      <div><span className="text-gray-500 text-sm">账号状态</span><p className="font-medium">已通过</p></div>
                      <div>
                        <span className="text-gray-500 text-sm">U币余额</span>
                        <div className="flex items-center justify-between mt-1">
                          <p className="font-bold text-lg text-orange-600">{teacherPointsMap[teacherDetailModal.teacher.id] || 0}</p>
                          <button
                            onClick={() => navigate(`/admin/point-transactions?userId=${teacherDetailModal.teacher.id}&role=teacher&returnUrl=/admin/teachers`)}
                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            查看明细 →
                          </button>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">绑定码</span>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-amber-600 text-lg tracking-wider">{teacherBindCode || '---'}</p>
                          {!isEditingBindCode ? (
                            <button
                              onClick={() => {
                                setBindCodeInput(teacherBindCode || '');
                                setIsEditingBindCode(true);
                              }}
                              className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
                            >
                              <Edit className="w-3 h-3" />修改
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={bindCodeInput}
                                onChange={(e) => setBindCodeInput(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                placeholder="输入3位数字"
                                maxLength={3}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                              />
                              <button
                                onClick={async () => {
                                  if (bindCodeInput.length !== 3) {
                                    setMessage({ type: 'error', text: '绑定码必须为3位数字' });
                                    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                                    return;
                                  }
                                  setIsSavingBindCode(true);
                                  const { error } = await supabase
                                    .from('profiles')
                                    .update({ bind_code: bindCodeInput })
                                    .eq('id', teacherDetailModal.teacher?.id);
                                  if (error) {
                                    setMessage({ type: 'error', text: '保存失败: ' + error.message });
                                  } else {
                                    setTeacherBindCode(bindCodeInput);
                                    setIsEditingBindCode(false);
                                    setMessage({ type: 'success', text: '绑定码已更新' });
                                    // 局部更新：直接更新 teachers 列表中对应教师的 bind_code
 setTeachers(prev => prev.map(t => t.id === teacherDetailModal.teacher?.id ? { ...t, bind_code: bindCodeInput } : t));
                                  }
                                  setIsSavingBindCode(false);
                                  setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                                }}
                                disabled={isSavingBindCode}
                                className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors disabled:opacity-50"
                              >
                                {isSavingBindCode ? '保存中...' : '保存'}
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingBindCode(false);
                                  setBindCodeInput('');
                                }}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400 transition-colors"
                              >
                                取消
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">姓名 <span className="text-red-500">*</span></label>
                          <input type="text" value={editTeacherForm.username} onChange={e => setEditTeacherForm({...editTeacherForm, username: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${editFormErrors.username ? 'border-red-500' : 'border-gray-200'}`} />
                          {editFormErrors.username && <p className="text-red-500 text-xs mt-1">{editFormErrors.username}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">手机号 <span className="text-red-500">*</span></label>
                          <input type="tel" value={editTeacherForm.phone} onChange={e => setEditTeacherForm({...editTeacherForm, phone: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${editFormErrors.phone ? 'border-red-500' : 'border-gray-200'}`} />
                          {editFormErrors.phone && <p className="text-red-500 text-xs mt-1">{editFormErrors.phone}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">省份 <span className="text-red-500">*</span></label>
                          <select value={editTeacherForm.province} onChange={e => handleEditProvinceChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg text-sm ${editFormErrors.province ? 'border-red-500' : 'border-gray-200'}`}>
                            <option value="">请选择省份</option>
                            {Object.keys(regionData).map(province => <option key={province} value={province}>{province}</option>)}
                          </select>
                          {editFormErrors.province && <p className="text-red-500 text-xs mt-1">{editFormErrors.province}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">城市 <span className="text-red-500">*</span></label>
                          <select value={editTeacherForm.city} onChange={e => setEditTeacherForm({...editTeacherForm, city: e.target.value})} disabled={!editTeacherForm.province} className={`w-full px-3 py-2 border rounded-lg text-sm ${editFormErrors.city ? 'border-red-500' : 'border-gray-200'} disabled:bg-gray-100`}>
                            <option value="">请选择城市</option>
                            {editAvailableCities.map(city => <option key={city} value={city}>{city}</option>)}
                          </select>
                          {editFormErrors.city && <p className="text-red-500 text-xs mt-1">{editFormErrors.city}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">单位性质 <span className="text-red-500">*</span></label>
                        <div className="flex gap-4">
                          {unitTypes.map(type => (
                            <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" value={type.value} checked={editTeacherForm.unit_type === type.value} onChange={e => setEditTeacherForm({...editTeacherForm, unit_type: e.target.value})} className="w-4 h-4 text-blue-500" />
                              <span className="text-sm text-gray-700">{type.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">单位名称 <span className="text-red-500">*</span></label>
                        <input type="text" value={editTeacherForm.unit_name} onChange={e => setEditTeacherForm({...editTeacherForm, unit_name: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${editFormErrors.unit_name ? 'border-red-500' : 'border-gray-200'}`} />
                        {editFormErrors.unit_name && <p className="text-red-500 text-xs mt-1">{editFormErrors.unit_name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">账号状态 <span className="text-red-500">*</span></label>
                        <select value={editTeacherForm.audit_status} onChange={e => setEditTeacherForm({...editTeacherForm, audit_status: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                          <option value="pending">待审核</option>
                          <option value="approved">已通过</option>
                          <option value="rejected">已拒绝</option>
                        </select>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={() => setIsEditingTeacher(false)} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">取消</button>
                        <button onClick={handleSaveTeacherEdit} className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">保存</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Edit className="w-4 h-4" />教师备注</h4>
                    {!isEditingRemark && (
                      <button
                        onClick={() => setIsEditingRemark(true)}
                        className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
                      >
                        <Edit className="w-3 h-3" />编辑
                      </button>
                    )}
                  </div>
                  {isEditingRemark ? (
                    <>
                      <textarea
                        value={teacherRemark}
                        onChange={e => setTeacherRemark(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                        placeholder="请输入教师备注信息..."
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <button
                          onClick={() => { setIsEditingRemark(false); setTeacherRemark(teacherDetailModal.teacher?.remark || ''); }}
                          className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                        >
                          取消
                        </button>
                        <button
                          onClick={handleSaveTeacherRemark}
                          disabled={isSavingRemark}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSavingRemark ? '保存中...' : '保存备注'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600 min-h-[24px]">
                      {teacherDetailModal.teacher?.remark || <span className="text-gray-400">无备注</span>}
                    </p>
                  )}
                </div>

                {(() => {
                  const now = new Date();
                  const activePackages = teacherAllPackages.filter(pkg => pkg.is_active !== false && new Date(pkg.expire_date) >= now);
                  const historyPackages = teacherAllPackages.filter(pkg => pkg.is_active === false || new Date(pkg.expire_date) < now);
                  return (
                    <>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Package className="w-4 h-4" />可用套餐 ({activePackages.length})</h4>
                        {activePackages.length > 0 ? (
                          <table className="w-full">
                            <thead className="bg-white rounded-lg">
                              <tr>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">考评体系</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">课程分类</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">级别分类</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">套餐类型</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">价格</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">实付</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">开通渠道</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">开通时间</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">到期时间</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">状态</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">操作</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {activePackages.map(pkg => {
                                const channelLabel = pkg.package_source === 'self' ? '自购' : '后台';
                                const createdTime = pkg.created_at ? formatDateTime(pkg.created_at) : '-';
                                return (
                                  <tr key={pkg.id} className="hover:bg-white rounded-lg">
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.organization}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.category}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.level_text}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{packageTypeMap[pkg.package_type]}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">¥{pkg.price}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">¥{pkg.actual_price || pkg.price}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{channelLabel}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{createdTime}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.expire_date}</td>
                                    <td className="px-2 py-2 whitespace-nowrap"><span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-600">生效中</span></td>
                                    <td className="px-2 py-2 whitespace-nowrap">
                                      <button
                                        onClick={() => setClosePackageModal({ open: true, teacher: teacherDetailModal.teacher, packageId: pkg.id })}
                                        className="px-2 py-1 text-xs rounded bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                      >
                                        关闭
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : <p className="text-gray-400 text-sm">暂无可用套餐</p>}
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Package className="w-4 h-4" />历史套餐 ({historyPackages.length})</h4>
                        {historyPackages.length > 0 ? (
                          <table className="w-full">
                            <thead className="bg-white rounded-lg">
                              <tr>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">考评体系</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">课程分类</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">级别分类</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">套餐类型</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">价格</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">实付</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">开通渠道</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">开通时间</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">到期时间</th>
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">状态</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {historyPackages.map(pkg => {
                                const isExpired = new Date(pkg.expire_date) < now;
                                const isClosed = pkg.is_active === false;
                                let statusLabel = '已过期';
                                let statusClass = 'bg-red-100 text-red-600';
                                if (isClosed) { statusLabel = '已关闭'; statusClass = 'bg-gray-100 text-gray-600'; }
                                const channelLabel = pkg.package_source === 'self' ? '自购' : '后台';
                                const createdTime = pkg.created_at ? formatDateTime(pkg.created_at) : '-';
                                return (
                                  <tr key={pkg.id} className="hover:bg-white rounded-lg">
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.organization}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.category}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.level_text}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{packageTypeMap[pkg.package_type]}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">¥{pkg.price}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">¥{pkg.actual_price || pkg.price}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{channelLabel}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{createdTime}</td>
                                    <td className="px-2 py-2 text-sm whitespace-nowrap">{pkg.expire_date}</td>
                                    <td className="px-2 py-2 whitespace-nowrap"><span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>{statusLabel}</span></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : <p className="text-gray-400 text-sm">暂无历史套餐</p>}
                      </div>
                    </>
                  );
                })()}

                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Receipt className="w-4 h-4" />订单记录</h4>
                  <OrderList
                    type="admin-teacher"
                    targetUid={teacherDetailModal.teacher?.id}
                    mode="modal"
                    enabled={!!teacherDetailModal.teacher}
                    showActions={false}
                    onViewRefundDetail={(order: any) => { setSelectedOrder(order); setShowRefundDetailModal(true); }}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button onClick={() => setTeacherDetailModal({ open: false })} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">关闭</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRefundDetailModal && selectedOrder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRefundDetailModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Eye className="w-5 h-5 text-blue-500" />退款详情</h3>
                <button onClick={() => setShowRefundDetailModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">订单号</span><span className="font-mono">{selectedOrder.orderNo || selectedOrder.id}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">实付金额</span><span className="text-gray-800">¥{Number(selectedOrder.actualPrice || 0).toFixed(2)}</span></div>
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-green-600" /></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款申请已处理</div>
                      <div className="text-xs text-gray-500">{formatDateTime(selectedOrder.refundedAt)}</div>
                    </div>
                  </div>
                  <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款金额</span><span className="font-bold text-red-600">¥{Number(selectedOrder.refundAmount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款去向</span><span className="text-gray-800">{selectedOrder.refundDestination === 'original' ? '原路退回' : '退回平台余额'}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款原因</span><span className="text-gray-800 max-w-[200px] text-right">{selectedOrder.refundReason || '-'}</span></div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Clock className="w-4 h-4 text-blue-600" /></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款到账中</div>
                      <div className="text-xs text-gray-500">预计1-3个工作日到账</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addTeacherModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-xl p-6 w-[500px] max-h-[90vh] overflow-auto">
              <h3 className="text-lg font-bold text-gray-800 mb-4">添加教师</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">姓名 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newTeacherForm.username}
                    onChange={e => setNewTeacherForm({...newTeacherForm, username: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.username ? 'border-red-500' : 'border-gray-200'}`}
                    placeholder="请输入姓名"
                  />
                  {formErrors.username && <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">手机号 <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    value={newTeacherForm.phone}
                    onChange={e => setNewTeacherForm({...newTeacherForm, phone: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.phone ? 'border-red-500' : 'border-gray-200'}`}
                    placeholder="请输入手机号"
                  />
                  {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">省份 <span className="text-red-500">*</span></label>
                    <select
                      value={newTeacherForm.province}
                      onChange={e => handleProvinceChange(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.province ? 'border-red-500' : 'border-gray-200'}`}
                    >
                      <option value="">请选择省份</option>
                      {Object.keys(regionData).map(province => (
                        <option key={province} value={province}>{province}</option>
                      ))}
                    </select>
                    {formErrors.province && <p className="text-red-500 text-xs mt-1">{formErrors.province}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">城市 <span className="text-red-500">*</span></label>
                    <select
                      value={newTeacherForm.city}
                      onChange={e => setNewTeacherForm({...newTeacherForm, city: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.city ? 'border-red-500' : 'border-gray-200'}`}
                      disabled={!newTeacherForm.province}
                    >
                      <option value="">请选择城市</option>
                      {availableCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                    {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">单位性质 <span className="text-red-500">*</span></label>
                  <div className="flex gap-4">
                    {unitTypes.map(type => (
                      <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value={type.value}
                          checked={newTeacherForm.unit_type === type.value}
                          onChange={e => setNewTeacherForm({...newTeacherForm, unit_type: e.target.value})}
                          className="w-4 h-4 text-blue-500"
                        />
                        <span className="text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">单位名称 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newTeacherForm.unit_name}
                    onChange={e => setNewTeacherForm({...newTeacherForm, unit_name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.unit_name ? 'border-red-500' : 'border-gray-200'}`}
                    placeholder="请输入单位名称"
                  />
                  {formErrors.unit_name && <p className="text-red-500 text-xs mt-1">{formErrors.unit_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">密码 <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={newTeacherForm.password}
                    onChange={e => setNewTeacherForm({...newTeacherForm, password: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.password ? 'border-red-500' : 'border-gray-200'}`}
                    placeholder="至少6位"
                  />
                  {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">确认密码 <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={newTeacherForm.confirmPassword}
                    onChange={e => setNewTeacherForm({...newTeacherForm, confirmPassword: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.confirmPassword ? 'border-red-500' : 'border-gray-200'}`}
                    placeholder="再次输入密码"
                  />
                  {formErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setAddTeacherModal(false); setFormErrors({}); }} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm">取消</button>
                <button onClick={handleAddTeacher} className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">确认添加</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {remarkModal.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-xl p-6 w-[400px]">
              <h3 className="text-lg font-bold text-gray-800 mb-4">编辑备注</h3>
              <textarea
                value={remarkInput}
                onChange={e => setRemarkInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                rows={4}
                placeholder="请输入备注内容"
              />
              <div className="flex gap-3 mt-6">
                <button onClick={() => setRemarkModal({ open: false })} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm">取消</button>
                <button onClick={handleSaveRemark} className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">保存</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* U币管理弹窗 */}
      {showPointModal && pointTeacher && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Crown className="w-5 h-5 text-orange-500" />
                U币管理 - {pointTeacher.username}
              </h3>
              <button onClick={() => setShowPointModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {pointSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-800 mb-2">操作成功</h4>
                <p className="text-gray-500 mb-4">
                  {pointAction === 'add' ? '增加' : pointAction === 'deduct' ? '扣减' : '赠送'}
                  U币 {pointAmount} 已完成
                </p>
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <span className="text-sm text-gray-600">当前U币余额：</span>
                  <span className="text-2xl font-bold text-orange-600 ml-2">{pointBalance}</span>
                </div>
                <button
                  onClick={() => { setShowPointModal(false); }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  完成
                </button>
              </div>
            ) : (
              <>
                {/* 当前U币余额 */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">当前U币余额</span>
                    <span className="text-3xl font-bold text-orange-600">{pointBalance}</span>
                  </div>
                </div>

                {/* 操作类型选择 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">操作类型</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setPointAction('add')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        pointAction === 'add'
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-green-300'
                      }`}
                    >
                      增加U币
                    </button>
                    <button
                      onClick={() => setPointAction('deduct')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        pointAction === 'deduct'
                          ? 'bg-red-50 border-red-500 text-red-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-red-300'
                      }`}
                    >
                      减少U币
                    </button>
                    <button
                      onClick={() => setPointAction('gift')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        pointAction === 'gift'
                          ? 'bg-purple-50 border-purple-500 text-purple-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'
                      }`}
                    >
                      赠送U币
                    </button>
                  </div>
                </div>

                {/* U币数量输入 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    U币数量 {pointAction === 'deduct' && <span className="text-red-500">(扣减后余额不能为负)</span>}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={pointAmount || ''}
                    onChange={(e) => setPointAmount(Math.max(0, parseInt(e.target.value) || 0))}
                    placeholder="请输入U币数量"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-500"
                  />
                  {pointAction === 'deduct' && pointAmount > pointBalance && (
                    <p className="text-red-500 text-xs mt-1">扣减数量不能超过当前余额 {pointBalance}</p>
                  )}
                </div>

                {/* 操作备注 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">操作备注</label>
                  <textarea
                    value={pointRemark}
                    onChange={(e) => setPointRemark(e.target.value)}
                    placeholder="请输入操作原因或备注信息..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500"
                  />
                </div>

                {/* 操作预览 */}
                {pointAmount > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-6 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">操作后余额预估：</span>
                      <span className={`font-bold ${
                        pointAction === 'add' || pointAction === 'gift' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {pointAction === 'deduct' ? pointBalance - pointAmount : pointBalance + pointAmount}
                      </span>
                    </div>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex gap-3">
                  <button
                    onClick={handlePointAction}
                    disabled={pointLoading || pointAmount <= 0 || (pointAction === 'deduct' && pointAmount > pointBalance)}
                    className={`flex-1 py-2 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                      pointAction === 'add' ? 'bg-green-600 hover:bg-green-700' :
                      pointAction === 'deduct' ? 'bg-red-600 hover:bg-red-700' :
                      'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {pointLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        处理中...
                      </span>
                    ) : (
                      `确认${pointAction === 'add' ? '增加' : pointAction === 'deduct' ? '减少' : '赠送'} ${pointAmount} U币`
                    )}
                  </button>
                  <button
                    onClick={() => setShowPointModal(false)}
                    className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    取消
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
